import React, { useMemo } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import Botao from '@/components/Botao';
import HBox from '@/components/layout/HBox';
import styles from './MiniCalendario.module.css';

const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const corPorEstado: Record<string, string> = {
  pendente: 'var(--tc-color-gray-400)',
  confirmada: 'var(--tc-color-success-500)',
  concluida: 'var(--tc-color-success-500)',
  cancelada: 'var(--tc-color-danger-500)',
};

export type MiniCalendarioProps = {
  mes: Date;
  onChangeMes: (mes: Date) => void;
  eventosPorDia: Record<string, any[]>;
  diaSelecionado?: string | null;
  onSelectDia: (dia: string) => void;
};

const MiniCalendario = ({
  mes,
  onChangeMes,
  eventosPorDia,
  diaSelecionado,
  onSelectDia,
}: MiniCalendarioProps) => {
  const dias = useMemo(() => {
    const inicioGrade = startOfWeek(startOfMonth(mes));
    const fimGrade = endOfWeek(endOfMonth(mes));
    return eachDayOfInterval({ start: inicioGrade, end: fimGrade });
  }, [mes]);

  return (
    <div className={styles.container}>
      <HBox className={styles.header}>
        <Botao
          variant="none-secondary"
          icon={mdiChevronLeft}
          onClick={() => onChangeMes(subMonths(mes, 1))}
          title="Mês anterior"
        />
        <div className={styles.mesLabel}>
          {format(mes, 'MMMM \'de\' yyyy', { locale: ptBR })}
        </div>
        <Botao
          variant="none-secondary"
          icon={mdiChevronRight}
          onClick={() => onChangeMes(addMonths(mes, 1))}
          title="Próximo mês"
        />
      </HBox>

      <div className={styles.grade}>
        {diasSemana.map(dia => (
          <div key={dia} className={styles.diaSemana}>
            {dia}
          </div>
        ))}

        {dias.map(dia => {
          const chave = format(dia, 'yyyy-MM-dd');
          const eventos = eventosPorDia[chave] ?? [];
          const foraDoMes = !isSameMonth(dia, mes);

          return (
            <button
              key={chave}
              type="button"
              className={[
                styles.dia,
                foraDoMes ? styles.foraDoMes : null,
                isToday(dia) ? styles.hoje : null,
                diaSelecionado === chave ? styles.selecionado : null,
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onSelectDia(chave)}
            >
              <span className={styles.numeroDia}>{format(dia, 'd')}</span>
              {eventos.length > 0 && (
                <span className={styles.bolinhas}>
                  {eventos.slice(0, 3).map((evento, idx) => (
                    <span
                      key={idx}
                      className={styles.bolinha}
                      style={{ backgroundColor: corPorEstado[evento.estado] ?? 'var(--tc-color-info-500)' }}
                    />
                  ))}
                  {eventos.length > 3 && (
                    <span className={styles.mais}>+{eventos.length - 3}</span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MiniCalendario;
